/**
 * Small derived accessors for app_feedback threads.
 *
 * Components in this directory consume the canonical Thread shape directly;
 * these helpers paper over (a) the `context.*` nesting for app_feedback fields
 * and (b) the fact that Cosmos `_ts` isn't on the public Thread type.
 *
 * Design: docs/FeatureDesigns/feedback-on-threads.md
 */

import { ADMIN_FEEDBACK_OWNER_ID } from '../threads/types';
import type { Thread, AppFeedbackContext, AppFeedbackKind } from '../threads/types';

/** Read the app_feedback context block. Returns {} for non-feedback threads. */
function ctx(thread: Thread): Partial<AppFeedbackContext> {
    return (thread.context ?? {}) as Partial<AppFeedbackContext>;
}

export function getFeedbackKind(thread: Thread): AppFeedbackKind | undefined {
    return ctx(thread).feedbackKind;
}

export function getSubmitterName(thread: Thread): string {
    return ctx(thread).userName ?? '';
}

export function getSubmitterRole(thread: Thread): 'student' | 'teacher' | 'admin' {
    return ctx(thread).userRole ?? 'student';
}

export function getSubmitterEmail(thread: Thread): string | undefined {
    return ctx(thread).userEmail;
}

export function getAppContext(thread: Thread): { url: string; userAgent: string } | undefined {
    return ctx(thread).appContext;
}

/**
 * Cosmos auto-populates `_ts` (seconds-since-epoch) on every read but it's
 * not on the public Thread type. Cast and convert to ISO; fall back to
 * editedAt / createdAt for non-Cosmos sources.
 */
export function getUpdatedAt(thread: Thread): string {
    const ts = (thread as Thread & { _ts?: number })._ts;
    if (typeof ts === 'number') return new Date(ts * 1000).toISOString();
    return thread.editedAt ?? thread.createdAt;
}

/**
 * Unseen under `seenKey` = never seen, OR a message `fromOther` newer than the
 * last look. Deliberately not "the thread changed since": the document moves on
 * any write (a status change with no message, a backfill touching every row),
 * which lit up nearly every row. Mirrors the server's
 * ThreadRepository.countUnseen, so the dots and the badge agree.
 */
function isUnreadFor(thread: Thread, seenKey: string, fromOther: (senderId: string) => boolean): boolean {
    const seenAt = thread.seenBy?.[seenKey];
    if (!seenAt) return true;
    return (thread.messages ?? []).some((m) => fromOther(m.senderId) && m.createdAt > seenAt);
}

/** Unread from the admin team's perspective (sentinel ownerId key). */
export function isUnreadByOwnerTeam(thread: Thread): boolean {
    // The team's "others" is the submitter — an admin's own reply isn't news to the team.
    return isUnreadFor(thread, ADMIN_FEEDBACK_OWNER_ID, (sender) => sender === thread.createdBy);
}

/** Unread from the creator's perspective — used by user-mode list views. */
export function isUnreadByCreator(thread: Thread): boolean {
    return isUnreadFor(thread, thread.createdBy, (sender) => sender !== thread.createdBy);
}
